import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiClientService, Artikl, Drzava, Firma, Javna_potraznja, Jedinica_mjere, Opi, Valutum } from '../../../services/api-client.service';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { CacheService } from '../../../services/cache.service';

@Component({
  selector: 'app-create-potraznja',
  templateUrl: './create-potraznja.component.html',
  styleUrls: ['./create-potraznja.component.scss']
})
export class CreatePotraznjaComponent {
  @Input() set visible(value: boolean) {
    this.isVisible = value;
    if (value) {
      this.initializeForm();
      this.loadLookupData();
    }
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() potraznjaCreated = new EventEmitter<Javna_potraznja>();

  isVisible = false;
  loading = false;
  dataLoading = false; // Separate loading for data
  potraznjaForm!: FormGroup;
  maxDate = new Date();

  // Lookup data
  artikli: Artikl[] = [];
  jediniceMjere: Jedinica_mjere[] = [];
  valute: Valutum[] = [];
  firme: Firma[] = [];
  opisi: Opi[] = [];
  drzave: Drzava[] = [];

  // Editor Options - Initialize with empty arrays
  readonlyEditorOptions = {
    readOnly: true,
    disabled: true
  };

  artiklEditorOptions: any = {
    dataSource: [],
    displayExpr: 'artikl1',
    valueExpr: 'id',
    searchEnabled: true,
    placeholder: 'Odaberite artikl...',
    disabled: true // Disable until data loads
  };

  opisEditorOptions: any = {
    dataSource: [],
    displayExpr: 'opis',
    valueExpr: 'id',
    placeholder: 'Odaberite opise...',
    searchEnabled: true,
    showSelectionControls: true,
    applyValueMode: 'instantly',
    multiline: true,
    disabled: true
  };

  kolicinaEditorOptions: any = {
    placeholder: 'Unesite količinu...',
    min: 1,
    showSpinButtons: true
  };

  jedinicaMjereEditorOptions: any = {
    dataSource: [],
    displayExpr: 'jedinica',
    valueExpr: 'id',
    placeholder: 'Odaberite jedinicu mjere...',
    disabled: true
  };

  fcoEditorOptions: any = {
    displayExpr: 'naziv',
    valueExpr: 'id',
    placeholder: 'Unesite paritet..',
    dataSource: [{ id: 0, naziv: 'FCA' }, { id: 1, naziv: 'FCO' }]
  };

  mjestoUtovaraEditorOptions: any = {
    placeholder: 'Unesite mjesto utovara...'
  };

  cijenaEditorOptions: any = {
    placeholder: 'Unesite cijenu...',
    min: 0,
    showSpinButtons: false
  };

  valutaEditorOptions: any = {
    dataSource: [],
    displayExpr: 'valuta',
    valueExpr: 'id',
    placeholder: 'Odaberite valutu...',
    disabled: true
  };

  pdvEditorOptions: any = {
    placeholder: 'Unesite PDV...'
  };

  nacinPlacanjaEditorOptions: any = {
    placeholder: 'Unesite način plaćanja...'
  };

  zaDrzavuEditorOptions: any = {
    dataSource: [],
    displayExpr: 'naziv',
    valueExpr: 'id',
    placeholder: 'Odaberite državu...',
    searchEnabled: true,
    disabled: true
  };

  zaDomaceEditorOptions: any = {
    text: 'Domaće'
  };

  zaIzvozEditorOptions: any = {
    dataSource: [],
    displayExpr: 'naziv',
    valueExpr: 'id',
    placeholder: 'Odaberite državu...',
    searchEnabled: true,
    disabled: true
  };

  dodatniOpisEditorOptions: any = {
    placeholder: 'Unesite dodatni opis...',
    autoResizeEnabled: true,
    height: 80
  };

  firmaEditorOptions: any = {
    dataSource: [],
    displayExpr: 'naziv',
    valueExpr: 'id',
    searchEnabled: true,
    placeholder: 'Odaberite firmu...',
    disabled: true,
    minSearchLength: 1,
    onValueChanged: (e: any) => {
      if (!e.value) return;

      const selected = this.firme.find((f: any) => f.id === e.value);
      if (selected) {
        // Update the bound form data directly
        this.potraznjaForm.value.selectedFirma = selected;

        // If you have form instance available (optional, for forcing UI refresh)
        if (e.component && e.component.option('form')) {
          const form = e.component.option('form');
          form.updateData('selectedFirma.telefon1', selected.telefon1);
        }
      }
    }
  };

  datumEditorOptions: any = {
    placeholder: 'Odaberite datum...',
    type: 'date',
    max: this.maxDate,
    displayFormat: "dd.MM.yyyy"
  };

  constructor(private fb: FormBuilder, private apiClient: ApiClientService, private cache: CacheService) {
    this.initializeForm();
  }

  initializeForm() {
    this.potraznjaForm = this.fb.group({
      artikl: [null, Validators.required],
      opis: [''],
      kolicina: [1, [Validators.required, Validators.min(1)]],
      jedinica_mjere: [null, Validators.required],
      fco: [{ id: 0, naziv: 'FCA' }],
      mjesto_utovara: [''],
      cijena: [0, [Validators.required, Validators.min(0)]],
      valuta: [null, Validators.required],
      pdv: [''],
      nacin_placanja: [''],
      za_drzavu: [null],
      za_domace: [0],
      za_izvoz: [null],
      dodatni_opis: [''],
      firma: [null, Validators.required],
      datum: [new Date().toISOString().split('T')[0], Validators.required],
      po_jedinica_mjere: [0],
      selectedFirma: {
        telefon1: ''
      }
    });
  }

  async loadLookupData() {
    this.dataLoading = true;

    try {
      // Load data in parallel
      const [artikliData, opisiData, jediniceMjereData, valuteData, firmeData, drzaveData] = await Promise.all([
        this.cache.getCachedOrFetch('artikli', () => this.apiClient.artikliAll()),
        this.cache.getCachedOrFetch('opisi', () => this.apiClient.opisiAll()),
        this.cache.getCachedOrFetch('jediniceMjere', () => this.apiClient.jediniceMjereAll()),
        this.cache.getCachedOrFetch('valute', () => this.apiClient.valuteAll()),
        this.cache.getCachedOrFetch('firme', () => this.apiClient.firmeAll(10000, 0)),
        this.cache.getCachedOrFetch('drzave', () => this.apiClient.drzaveAll())
      ]);

      // Assign the data
      this.artikli = artikliData;
      this.opisi = opisiData;
      this.jediniceMjere = jediniceMjereData;
      this.valute = valuteData;
      this.firme = firmeData;
      this.drzave = drzaveData;

      // Update editor options with loaded data and enable them
      this.updateEditorOptions();

    } catch (error) {
      console.error('Error loading lookup data:', error);
    } finally {
      this.dataLoading = false;
    }
  }

  private updateEditorOptions() {
    // Update data sources and enable controls
    this.artiklEditorOptions = {
      ...this.artiklEditorOptions,
      dataSource: this.artikli,
      disabled: false
    };

    this.opisEditorOptions = {
      ...this.opisEditorOptions,
      dataSource: this.opisi,
      disabled: false
    };

    this.jedinicaMjereEditorOptions = {
      ...this.jedinicaMjereEditorOptions,
      dataSource: this.jediniceMjere,
      disabled: false
    };

    this.valutaEditorOptions = {
      ...this.valutaEditorOptions,
      dataSource: this.valute,
      disabled: false
    };

    this.firmaEditorOptions = {
      ...this.firmaEditorOptions,
      dataSource: new DataSource({
        store: new ArrayStore({
          data: this.firme,
          key: 'id',
        }),
        paginate: true,
        pageSize: 50,
      }),
      disabled: false
    };

    this.zaDrzavuEditorOptions = {
      ...this.zaDrzavuEditorOptions,
      dataSource: this.drzave,
      disabled: false
    };

    this.zaIzvozEditorOptions = {
      ...this.zaIzvozEditorOptions,
      dataSource: this.drzave,
      disabled: false
    };

    // Force refresh of the form component
    // This is a workaround to make DevExtreme refresh the data sources
    setTimeout(() => {
      // You might need to manually trigger a refresh here
      // If the form doesn't update, you can try recreating the form
    }, 0);
  }

  onFieldDataChanged(e: any) {
    if (e.dataField && e.value !== undefined) {
      this.potraznjaForm.patchValue({
        [e.dataField]: e.value
      });
    }
  }

  onPopupHiding() {
    this.isVisible = false;
    this.visibleChange.emit(false);
  }

  onCancel() {
    this.isVisible = false;
    this.visibleChange.emit(false);
    this.potraznjaForm.reset();
  }

  async onSubmit() {
    if (this.potraznjaForm.valid) {
      this.loading = true;

      delete this.potraznjaForm.value.selectedFirma; // Remove temporary field
      try {
        const formData: Javna_potraznja = {
          id: 0,
          ...this.potraznjaForm.value,
          opis: this.convertOpisToText(this.potraznjaForm.value.opis),
          za_drzavu: this.potraznjaForm.value.za_drzavu ? this.potraznjaForm.value.za_drzavu : 0,
          za_domace: this.potraznjaForm.value.za_domace ? 1 : 0,
          za_izvoz: this.potraznjaForm.value.za_izvoz ? this.potraznjaForm.value.za_izvoz : 0
        };

        await this.apiClient.potraznjePOST(formData);

        this.potraznjaCreated.emit(formData);
        console.log(formData);
        this.isVisible = false;
        this.visibleChange.emit(false);
        this.potraznjaForm.reset();

      } catch (error) {
        console.error('Error creating potraznja:', error);
      } finally {
        this.loading = false;
      }
    } else {
      this.markFormGroupTouched(this.potraznjaForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private convertOpisToText(selectedIds: number[]): string {
    if (!selectedIds || selectedIds.length === 0) return '';

    const selectedTexts = selectedIds.map(id => {
      const option = this.opisi.find(op => op.id === id);
      return option ? option.opis : '';
    }).filter(text => text !== '');

    return selectedTexts.join(', ');
  }
}